import { useEffect, useState, useCallback } from 'react';
import {
  Clock,
  Send,
  AlertCircle,
  CheckCircle2,
  Users,
  MapPin,
  UserCheck,
  Trash2,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import {
  pushAPI,
  type PushNotificationType,
  type ScheduleBody,
  type ScheduledPush,
  type ScheduledStatus,
} from '../../services/api';

type TargetMode = 'userIds' | 'filter' | 'todos';

const TYPES: { value: PushNotificationType; label: string }[] = [
  { value: 'fatura', label: 'Fatura' },
  { value: 'suporte', label: 'Suporte' },
  { value: 'promocao', label: 'Promoção' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'sistema', label: 'Sistema' },
  { value: 'custom', label: 'Custom' },
];

const STATUS_STYLE: Record<ScheduledStatus, string> = {
  pending: 'bg-primary-soft/30 text-primary border border-primary-line/50',
  sent: 'bg-success-soft/30 text-success border border-success-line/50',
  failed: 'bg-danger-soft/30 text-danger border border-danger-line/50',
  cancelled: 'bg-hover text-fg-2 border border-active',
};

const STATUS_LABEL: Record<ScheduledStatus, string> = {
  pending: 'Agendado',
  sent: 'Enviado',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

/**
 * ProgramarTab — agendamento de push notifications pra disparo futuro.
 *
 * Worker do backend (services/api/src/modules/core/push/scheduled/worker.ts)
 * varre a cada 30s e dispara os agendamentos com scheduledFor <= now.
 */
export default function ProgramarTab() {
  const [showForm, setShowForm] = useState(false);
  const [refresh, setRefresh] = useState(0);

  return (
    <div className="space-y-6">
      {showForm ? (
        <ScheduleForm
          onClose={() => setShowForm(false)}
          onScheduled={() => {
            setShowForm(false);
            setRefresh((r) => r + 1);
          }}
        />
      ) : (
        <div className="flex items-center justify-between bg-surface border border-line rounded-2xl p-5">
          <div>
            <h2 className="text-lg font-semibold text-fg flex items-center gap-2">
              <Clock size={18} className="text-primary" />
              Programar envio
            </h2>
            <p className="text-sm text-fg-2 mt-1">
              Agenda push pra disparar em data e hora específicas. Worker verifica a cada 30s.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-solid hover:bg-primary-solid-hover text-white rounded-lg text-sm font-medium transition"
          >
            <Calendar size={16} />
            Novo agendamento
          </button>
        </div>
      )}

      <ScheduledList key={refresh} />
    </div>
  );
}

// ===== Form =====

function ScheduleForm({
  onClose,
  onScheduled,
}: {
  onClose: () => void;
  onScheduled: () => void;
}) {
  const [targetMode, setTargetMode] = useState<TargetMode>('userIds');
  const [userIdsText, setUserIdsText] = useState('');
  const [cidade, setCidade] = useState('');
  const [bairro, setBairro] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<PushNotificationType>('sistema');
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const [scheduledLocal, setScheduledLocal] = useState(() => defaultScheduleLocal());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  function parseUserIds(): string[] {
    return userIdsText
      .split(/[;\n\r,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!title.trim() || !body.trim()) {
      setError('Título e mensagem são obrigatórios.');
      return;
    }
    if (!scheduledLocal) {
      setError('Defina data e hora.');
      return;
    }

    const scheduledDate = new Date(scheduledLocal);
    if (Number.isNaN(scheduledDate.getTime())) {
      setError('Data inválida.');
      return;
    }
    if (scheduledDate.getTime() < Date.now()) {
      setError('Data agendada deve ser no futuro.');
      return;
    }

    const target: ScheduleBody['target'] = {};
    if (targetMode === 'userIds') {
      const ids = parseUserIds();
      if (ids.length === 0) {
        setError('Forneça ao menos 1 CPF/CNPJ.');
        return;
      }
      target.userIds = ids;
    } else if (targetMode === 'filter') {
      if (!cidade.trim() && !bairro.trim()) {
        setError('Informe cidade e/ou bairro.');
        return;
      }
      target.filter = {};
      if (cidade.trim()) target.filter.cidade = cidade.trim();
      if (bairro.trim()) target.filter.bairro = bairro.trim();
    } else {
      // Broadcast: todos os devices ativos no momento do disparo
      target.broadcast = true;
    }

    setLoading(true);
    try {
      await pushAPI.schedule({
        scheduledFor: scheduledDate.toISOString(),
        type,
        target,
        payload: { title: title.trim(), body: body.trim(), priority },
      });
      setSuccess(true);
      setTimeout(() => {
        onScheduled();
      }, 800);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao agendar.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-fg flex items-center gap-2">
          <Calendar size={18} className="text-primary" />
          Novo agendamento
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-fg-2 hover:text-fg text-sm"
        >
          Cancelar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-fg-2 mb-2">
            Data e hora do envio
          </label>
          <input
            type="datetime-local"
            value={scheduledLocal}
            onChange={(e) => setScheduledLocal(e.target.value)}
            min={nowLocal()}
            required
            className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg-2 mb-2">Destinatários</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <TargetCard
              active={targetMode === 'userIds'}
              icon={<UserCheck size={18} />}
              label="Por CPF/CNPJ"
              desc="Lista específica"
              onClick={() => setTargetMode('userIds')}
            />
            <TargetCard
              active={targetMode === 'filter'}
              icon={<MapPin size={18} />}
              label="Por região"
              desc="Cidade / bairro"
              onClick={() => setTargetMode('filter')}
            />
            <TargetCard
              active={targetMode === 'todos'}
              icon={<Users size={18} />}
              label="Todos"
              desc="Broadcast em massa"
              onClick={() => setTargetMode('todos')}
            />
          </div>
        </div>

        {targetMode === 'todos' && (
          <div className="rounded-lg border border-warning-line/50 bg-warning-soft/20 px-4 py-3 text-sm text-warning">
            Será enviado pra <strong>todos os dispositivos ativos</strong> no momento do
            disparo agendado.
          </div>
        )}

        {targetMode === 'userIds' && (
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              CPFs/CNPJs (vírgula, ponto-e-vírgula ou linha)
            </label>
            <textarea
              value={userIdsText}
              onChange={(e) => setUserIdsText(e.target.value)}
              rows={3}
              placeholder="12345678900&#10;98765432100"
              className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
            />
            <p className="text-xs text-muted mt-1">{parseUserIds().length} destinatário(s)</p>
          </div>
        )}

        {targetMode === 'filter' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Cidade" value={cidade} onChange={setCidade} placeholder="ex: São Paulo" />
            <Field label="Bairro (opcional)" value={bairro} onChange={setBairro} placeholder="ex: Centro" />
          </div>
        )}

        <div className="border-t border-line pt-5 space-y-4">
          <Field label="Título" value={title} onChange={setTitle} required placeholder="Ex: Manutenção programada" />
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1">Mensagem</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              required
              placeholder="Conteúdo da notificação..."
              className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Tipo</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as PushNotificationType)}
                className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Prioridade</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'normal' | 'high')}
                className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
              >
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-danger-soft/20 border border-danger-line/50 rounded-lg text-sm text-danger">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 p-3 bg-success-soft/20 border border-success-line/50 rounded-lg text-sm text-success">
            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
            <span>Agendamento criado com sucesso!</span>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active text-white rounded-lg text-sm font-medium transition"
          >
            <Send size={16} />
            {loading ? 'Agendando…' : 'Agendar'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ===== Lista =====

function ScheduledList() {
  const [items, setItems] = useState<ScheduledPush[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ScheduledStatus | ''>('pending');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await pushAPI.listScheduled({
        status: (statusFilter || undefined) as ScheduledStatus | undefined,
        limit: 50,
      });
      setItems(r.data);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao carregar.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCancel(item: ScheduledPush): Promise<void> {
    if (!confirm(`Cancelar agendamento "${item.payload.title}"?`)) return;
    try {
      await pushAPI.cancelScheduled(item.id);
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(e.response?.data?.message ?? e.message ?? 'Erro ao cancelar.');
    }
  }

  function fmtTarget(t: ScheduledPush['target']): string {
    if (t.userIds?.length) return `${t.userIds.length} CPF(s)`;
    if (t.filter) {
      const parts = [t.filter.cidade, t.filter.bairro].filter(Boolean);
      return `Região: ${parts.join(' / ') || '—'}`;
    }
    return '—';
  }

  function fmtDate(iso: string): string {
    const d = new Date(iso);
    return (
      d.toLocaleDateString('pt-BR') +
      ' ' +
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    );
  }

  return (
    <div className="bg-surface border border-line rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-fg">Agendamentos</h3>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ScheduledStatus | '')}
            className="px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm focus:border-primary focus:outline-none"
          >
            <option value="">Todos</option>
            <option value="pending">Agendados</option>
            <option value="sent">Enviados</option>
            <option value="failed">Falhados</option>
            <option value="cancelled">Cancelados</option>
          </select>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="p-2 bg-canvas hover:bg-hover border border-line rounded-lg transition"
            aria-label="Atualizar"
          >
            <RefreshCw
              size={14}
              className={loading ? 'animate-spin text-primary' : 'text-fg-2'}
            />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-danger-soft/20 border border-danger-line/50 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <p className="text-center text-muted py-8">Carregando…</p>
      ) : items.length === 0 ? (
        <p className="text-center text-muted py-8">
          {statusFilter === 'pending'
            ? 'Nenhum agendamento ativo. Use o botão acima pra criar.'
            : 'Nenhum agendamento.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className="bg-canvas border border-line rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[item.status]}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                    <span className="text-xs text-muted">{item.type}</span>
                  </div>
                  <h4 className="text-sm font-medium text-fg truncate">
                    {item.payload.title}
                  </h4>
                  <p className="text-xs text-fg-2 truncate">{item.payload.body}</p>
                </div>
                {item.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => void handleCancel(item)}
                    className="flex items-center gap-1 px-2 py-1 bg-danger-soft/20 hover:bg-danger-soft/40 border border-danger-line/50 text-danger rounded text-xs transition"
                  >
                    <Trash2 size={12} />
                    Cancelar
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted mt-2">
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {fmtDate(item.scheduledFor)}
                </span>
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {fmtTarget(item.target)}
                </span>
                {item.resultSummary && (
                  <span className="text-fg-2">
                    Enviados: {item.resultSummary.sent} · {item.resultSummary.devicesCount} device(s)
                  </span>
                )}
              </div>
              {item.failedReason && (
                <p className="text-xs text-danger mt-2">{item.failedReason}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ===== Helpers =====

function TargetCard({
  active,
  icon,
  label,
  desc,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
        active
          ? 'border-primary bg-primary-soft/20'
          : 'border-line bg-canvas hover:border-active'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
          active ? 'bg-primary-solid text-white' : 'bg-hover text-fg-2'
        }`}
      >
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-fg">{label}</div>
        <div className="text-xs text-muted">{desc}</div>
      </div>
    </button>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-2 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 bg-canvas border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none"
      />
    </div>
  );
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Valor default do input: agora + 30min (sugestão). */
function defaultScheduleLocal(): string {
  return toLocalInput(new Date(Date.now() + 30 * 60_000));
}

/** Min do input: agora (deixa user escolher qualquer horário futuro). */
function nowLocal(): string {
  return toLocalInput(new Date());
}
